import { useAuthStore } from '@/stores/auth-store';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * T-104 (P0): errors thrown by this client used to be bare `Error`s, so every
 * caller could tell was "something went wrong". The article page turned that
 * into "Post not found / This post may have been removed" — an assertion it
 * could not possibly make. A 500, a 429, or a dropped connection all told the
 * reader the article had been DELETED, which sends them away permanently and
 * hides real outages from us (it lied to QA about what was broken).
 *
 * `status` is the fact the UI actually needs. Only 404 means "gone"; anything
 * else means "we could not load it, try again".
 */
/** T-174: `{ fieldName: "why it failed" }` as sent by the backend validator. */
export type FieldErrors = Record<string, string>;

export function isFieldErrors(v: unknown): v is FieldErrors {
  return (
    !!v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.values(v as Record<string, unknown>).every((m) => typeof m === 'string')
  );
}

/**
 * T-174: turn the backend's per-field detail into one human sentence.
 * Prefixed with the field name so "Invalid email" becomes "Email: Invalid
 * email" — the user has four boxes and needs to know which one.
 */
export function fieldMessage(details: unknown): string | null {
  if (!isFieldErrors(details)) return null;
  const entries = Object.entries(details);
  if (entries.length === 0) return null;
  return entries
    .map(([field, msg]) => {
      if (field === '_form') return msg;
      const label = field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
      return `${label}: ${msg}`;
    })
    .join('. ');
}

export class ApiError extends Error {
  readonly status: number;
  /** No HTTP response at all: DNS, offline, CORS, aborted connection. */
  readonly isNetworkError: boolean;
  /** T-174: per-field validation detail, so a form can mark the guilty input. */
  readonly fieldErrors?: FieldErrors;

  constructor(
    message: string,
    status: number,
    isNetworkError = false,
    fieldErrors?: FieldErrors,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.isNetworkError = isNetworkError;
    this.fieldErrors = fieldErrors;
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /** The resource genuinely does not exist. The ONLY case worth telling a reader "not found". */
  get isNotFound() {
    return this.status === 404;
  }

  /** Rate limited — always transient, always our fault, never the reader's. */
  get isRateLimited() {
    return this.status === 429;
  }
}

/**
 * Classify anything thrown out of the api client, including bare Errors from
 * older call sites. Unknown failures are treated as "temporarily unavailable",
 * never as "not found": claiming a thing is deleted is the more damaging lie.
 */
export function describeApiError(err: unknown): {
  kind: 'not-found' | 'offline' | 'rate-limited' | 'server' | 'unauthorized' | 'unknown';
  status: number;
} {
  if (err instanceof ApiError) {
    if (err.isNetworkError) return { kind: 'offline', status: 0 };
    if (err.status === 404) return { kind: 'not-found', status: 404 };
    if (err.status === 429) return { kind: 'rate-limited', status: 429 };
    if (err.status === 401 || err.status === 403) return { kind: 'unauthorized', status: err.status };
    if (err.status >= 500) return { kind: 'server', status: err.status };
    return { kind: 'unknown', status: err.status };
  }
  return { kind: 'unknown', status: 0 };
}

/**
 * T-113 (P0): the array fields on a Post are NULLABLE in Postgres
 * (backend/src/db/schema/posts.ts declares `tags`/`brands`/`vehicleTypes`
 * as `.array()` with NO `.notNull()` and no default), yet the `Post` TS type
 * declares them `string[]`. Any row the entity-tagger never processed arrives
 * as JSON `null`, and `post.tags.length` throws
 * "Cannot read properties of null (reading 'length')".
 *
 * Treat null and absent identically for EVERY array the post carries, at the
 * one boundary they enter the app through. A `?.` on whichever line happened
 * to throw leaves the same landmine on the other thirteen.
 */
export function normalizePostArrays<
  T extends { tags?: unknown; brands?: unknown; vehicleTypes?: unknown },
>(post: T): T {
  if (!post) return post;
  // Constrained on the three array fields ONLY, never `Record<string, unknown>`:
  // that constraint widens `Post` to `{}` and every downstream `post.title`
  // stops type-checking. The generic must preserve T, not replace it.
  return {
    ...post,
    tags: Array.isArray(post.tags) ? post.tags : [],
    brands: Array.isArray(post.brands) ? post.brands : [],
    vehicleTypes: Array.isArray(post.vehicleTypes) ? post.vehicleTypes : [],
  };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;

    // Build URL with query params
    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Get auth token
    const token = useAuthStore.getState().accessToken;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    // T-104: a dropped connection must not surface as an indistinguishable
    // Error that the UI reads as "this post does not exist".
    let response: Response;
    try {
      response = await fetch(url, {
        ...fetchOptions,
        headers,
      });
    } catch (e) {
      throw new ApiError(
        e instanceof Error ? e.message : 'Network request failed',
        0,
        true
      );
    }

    // T-112 (P0): the LOGIN/REGISTER call's own 401 is not a session event.
    //
    // A 401 from `/auth/login` means "those credentials are wrong". It is the
    // endpoint's ANSWER, not a signal that our session lapsed. Feeding it to
    // the refresh-and-logout handler below did three wrong things at once: it
    // tried to refresh a token we do not have, it called logout() on a user
    // who was never logged in, and — the visible bug — it threw away the
    // server's real message ("Invalid credentials") and replaced it with the
    // generic 'Not authenticated', which tells the user nothing.
    //
    // Let the server's own error text through so the form can say what is
    // actually wrong. Decision 89: an error state must not assert what it
    // cannot know, and it must not discard what it does know either.
    const isAuthAttempt =
      endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/register');

    // Handle token refresh if needed
    if (response.status === 401 && !isAuthAttempt) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the request
        const newToken = useAuthStore.getState().accessToken;
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, {
          ...fetchOptions,
          headers,
        });
        if (!retryResponse.ok) {
          throw new ApiError(`API Error: ${retryResponse.status}`, retryResponse.status);
        }
        return retryResponse.json();
      } else {
        // T-99 (P0): a 401 only means "your SESSION EXPIRED" if you had one.
        //
        // This branch used to log out and hard-redirect to /auth/login
        // unconditionally, which walled the front door: an anonymous visitor
        // landing on `/` fires feed/notification requests, some 401, and the
        // API CLIENT bounced them to a login form. It is a
        // `window.location.href`, not a router navigation, so T-76's
        // route-level deny-list could never see it and no route was ever
        // "public" in practice.
        //
        // Nothing that curl or the API funnel check can observe, either: the
        // server returns a correct 200 CSR shell and a correct 401, and the
        // damage happens in the browser afterwards.
        const hadSession = useAuthStore.getState().isAuthenticated;
        useAuthStore.getState().logout();
        if (hadSession) {
          // A real expiry for a real user: bouncing to login is right.
          window.location.href = '/auth/login';
        }
        // Anonymous: stay put. The caller decides what an unauthenticated
        // response means for it — most callers render a signed-out variant.
        throw new ApiError(hadSession ? 'Session expired' : 'Not authenticated', 401);
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      // T-174: the backend already sends WHICH field failed and why, in
      // `details` ({ email: "Invalid email", ... } — see backend
      // lib/validator.ts formatZodErrors). We were throwing away that object
      // and surfacing only the generic top-level `error`, so every failed
      // signup read "Validation failed" and the user had no idea which box to
      // fix. Prefer the specific message; fall back to the generic one.
      // Decision 89 holds: we relay the server's own text, never invent a
      // diagnosis of our own.
      throw new ApiError(
        fieldMessage(error.details) || error.error || `API Error: ${response.status}`,
        response.status,
        false,
        isFieldErrors(error.details) ? error.details : undefined,
      );
    }

    return response.json();
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    }
  }

  // Auth endpoints
  auth = {
    register: (data: { email: string; username: string; password: string; displayName?: string }) =>
      this.request<{ user: User; accessToken: string; refreshToken: string }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    login: (data: { email: string; password: string }) =>
      this.request<{ user: User; accessToken: string; refreshToken: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    me: () => this.request<User>('/auth/me'),

    // T-197: makes the Privacy page's "delete your account from your account
    // settings" sentence true. Irreversible server-side.
    deleteAccount: () =>
      this.request<{ message: string }>('/auth/account', { method: 'DELETE' }),
  };

  // Feed endpoints
  feed = {
    get: (params: FeedParams) =>
      this.request<PaginatedResponse<Post>>('/feed', { params: params as Record<string, string> }),

    bookmarks: (params?: { offset?: number; limit?: number }) =>
      this.request<PaginatedResponse<Post>>('/feed/bookmarks', { params: params as Record<string, string> }),

    bookmarksCount: () =>
      this.request<{ count: number }>('/feed/bookmarks/count'),

    vehicleClusters: (params?: { limit?: number; days?: number }) =>
      this.request<{ items: VehicleCluster[] }>('/feed/vehicle-clusters', { params: params as Record<string, string> }),

    vehicleClusterPosts: (slug: string, params?: { offset?: number; limit?: number; days?: number }) =>
      this.request<{ items: Post[]; nextOffset: number | null }>(`/feed/vehicle-clusters/${encodeURIComponent(slug)}`, { params: params as Record<string, string> }),

    history: (params?: { offset?: number; limit?: number }) =>
      this.request<PaginatedResponse<Post>>('/feed/history', { params: params as Record<string, string> }),

    dailyBriefing: () =>
      this.request<{ items: BriefingItem[]; personalized?: boolean; generatedAt: string }>('/feed/daily-briefing'),

    related: (postId: string, params?: { limit?: number }) =>
      this.request<{ items: RelatedArticle[] }>(`/feed/related/${encodeURIComponent(postId)}`, { params: params as Record<string, string> }),

    garageTrending: (params?: { limit?: number; days?: number }) =>
      this.request<GarageTrendingResponse>('/feed/garage-trending', { params: params as Record<string, string> }),
  };

  // ENGAGE-015: Morning Dispatch endpoints
  dispatch = {
    morning: () =>
      this.request<DispatchResponse>('/dispatch/morning'),

    preferences: () =>
      this.request<{ dispatchEnabled: boolean; dispatchLastSentAt: string | null; isPro: boolean }>('/dispatch/preferences'),

    subscribe: () =>
      this.request<{ success: boolean; dispatchEnabled: boolean }>('/dispatch/subscribe', { method: 'POST' }),

    unsubscribe: () =>
      this.request<{ success: boolean; dispatchEnabled: boolean }>('/dispatch/unsubscribe', { method: 'POST' }),
  };

  // Post endpoints
  posts = {
    get: async (id: string) => {
      const res = await this.request<{ post: Post; userInteraction: UserInteraction; discussingGarages: DiscussingGarage[] }>(`/posts/${id}`);
      // T-113 (P0): `tags`, `brands` and `vehicleTypes` are NULLABLE Postgres
      // array columns (backend/src/db/schema/posts.ts has no .notNull() on
      // them), but the `Post` TS type declares them `string[]`. That type is a
      // LIE for any row where the tagger never ran, and JSON null flows
      // straight through to `post.tags.length` -> "Cannot read properties of
      // null (reading 'length')".
      //
      // Normalising here rather than at the ~14 call sites is deliberate:
      // the class is "an array field the type promises but the DB may omit",
      // and one boundary that cannot be forgotten beats a `?.` per crash.
      return { ...res, post: normalizePostArrays(res.post), discussingGarages: res.discussingGarages ?? [] };
    },

    upvote: (id: string) =>
      this.request<{ upvoted: boolean }>(`/posts/${id}/upvote`, { method: 'POST' }),

    downvote: (id: string) =>
      this.request<{ downvoted: boolean }>(`/posts/${id}/downvote`, { method: 'POST' }),

    bookmark: (id: string) =>
      this.request<{ bookmarked: boolean }>(`/posts/${id}/bookmark`, { method: 'POST' }),

    notRelevant: (id: string) =>
      this.request<{ notRelevant: boolean }>(`/posts/${id}/not-relevant`, { method: 'POST' }),

    react: (id: string, type: 'fire' | 'like' | 'interesting' | null) =>
      this.request<{ reaction: 'fire' | 'like' | 'interesting' | null }>(`/posts/${id}/react`, {
        method: 'POST',
        body: JSON.stringify({ type }),
      }),

    getComments: (id: string, params?: { cursor?: string; limit?: number }) =>
      this.request<PaginatedResponse<Comment>>(`/posts/${id}/comments`, { params: params as Record<string, string> }),

    addComment: (id: string, data: { content: string; parentId?: string; highlightedText?: string }) =>
      this.request<Comment>(`/posts/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    upvoteComment: (postId: string, commentId: string) =>
      this.request<{ upvoted: boolean }>(`/posts/${postId}/comments/${commentId}/upvote`, { method: 'POST' }),

    downvoteComment: (postId: string, commentId: string) =>
      this.request<{ downvoted: boolean }>(`/posts/${postId}/comments/${commentId}/downvote`, { method: 'POST' }),

    reactToComment: (postId: string, commentId: string, reaction: CommentReactionEmoji) =>
      this.request<CommentReactionToggleResult>(`/posts/${postId}/comments/${commentId}/react`, {
        method: 'POST',
        body: JSON.stringify({ reaction }),
      }),

    getCommentReactions: (postId: string, commentId: string) =>
      this.request<CommentReactionData>(`/posts/${postId}/comments/${commentId}/reactions`),

    getCommentReactionsBulk: (postId: string) =>
      this.request<BulkCommentReactions>(`/posts/${postId}/comments/reactions/bulk`),

    // ENGAGE-014: Garage discussion threads linked to this article
    getGarageThreads: (postId: string) =>
      this.request<{ threads: GarageThread[] }>(`/posts/${postId}/garage-threads`),
  };

  // Garage endpoints
  garages = {
    list: (params?: { search?: string; category?: string; cursor?: string; limit?: number; sort?: 'members' | 'recently_active' }) =>
      this.request<PaginatedResponse<Garage>>('/garages', { params: params as Record<string, string> }),

    trending: (params?: { limit?: number; days?: number }) =>
      this.request<{ items: TrendingGarage[] }>('/garages/trending', { params: params as Record<string, string> }),

    spotlight: (params?: { limit?: number }) =>
      this.request<{ items: SpotlightEntry[] }>('/garages/spotlight', { params: params as Record<string, string> }),

    trendingDiscussions: (params?: { limit?: number; days?: number }) =>
      this.request<{ items: TrendingDiscussion[] }>('/garages/trending-discussions', { params: params as Record<string, string> }),

    activityFeed: (params?: { limit?: number }) =>
      this.request<{ items: GarageActivityEvent[] }>('/garages/activity-feed', { params: params as Record<string, string> }),

    count: (vehicleSlug: string, brand?: string) =>
      this.request<GarageCountResponse>(`/garages/count/${encodeURIComponent(vehicleSlug)}`, {
        params: brand ? { brand } : undefined,
      }),

    my: () => this.request<{ items: Garage[] }>('/garages/my'),

    get: (slug: string) => this.request<Garage>(`/garages/${slug}`),

    create: (data: { name: string; description?: string; emoji?: string; category?: string; isPublic?: boolean }) =>
      this.request<Garage>('/garages', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    join: (slug: string) =>
      this.request<{ message: string }>(`/garages/${slug}/join`, { method: 'POST' }),

    leave: (slug: string) =>
      this.request<{ message: string }>(`/garages/${slug}/leave`, { method: 'POST' }),

    getPosts: (slug: string, params?: { cursor?: string; limit?: number }) =>
      this.request<PaginatedResponse<GaragePost>>(`/garages/${slug}/posts`, { params: params as Record<string, string> }),

    sharePost: (slug: string, postId: string) =>
      this.request<GaragePost>(`/garages/${slug}/posts`, {
        method: 'POST',
        body: JSON.stringify({ postId }),
      }),

    createPost: (slug: string, data: { title?: string; content?: string; imageUrls?: string[] }) =>
      this.request<GaragePost>(`/garages/${slug}/posts`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getPostComments: (garagePostId: string) =>
      this.request<{ items: GaragePostComment[] }>(`/garages/posts/${garagePostId}/comments`),

    addPostComment: (garagePostId: string, data: { content: string; parentId?: string }) =>
      this.request<GaragePostComment>(`/garages/posts/${garagePostId}/comments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // COMMUNITY-003: Vote on garage posts and comments
    votePost: (garagePostId: string, voteType: 'up' | 'down' | 'none') =>
      this.request<{ voteType: string; garagePostId: string }>(`/garages/posts/${garagePostId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ voteType }),
      }),

    voteComment: (garageCommentId: string, voteType: 'up' | 'down' | 'none') =>
      this.request<{ voteType: string; garageCommentId: string }>(`/garages/comments/${garageCommentId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ voteType }),
      }),

    getMyPostVotesBatch: (postIds: string[]) =>
      this.request<{ votes: Record<string, string> }>(`/garages/posts/votes-batch`, {
        method: 'POST',
        body: JSON.stringify({ postIds }),
      }),

    // COMM-017: Garage-specific activity feed
    getActivity: (slug: string, params?: { limit?: number }) =>
      this.request<{ items: GarageSlugActivityEvent[]; garageSlug: string; garageName: string }>(
        `/garages/${slug}/activity`,
        { params: params as Record<string, string> },
      ),

    // COMM-011: "Members also in" co-membership discovery
    getAlsoIn: (slug: string, params?: { limit?: number }) =>
      this.request<{ items: GarageAlsoInItem[] }>(
        `/garages/${slug}/also-in`,
        { params: params as Record<string, string> },
      ),

    // COMM-010: Reading activity stream
    getReadingActivity: (slug: string, params?: { limit?: number }) =>
      this.request<{ items: GarageReadingEvent[]; garageSlug: string }>(
        `/garages/${slug}/reading-activity`,
        { params: params as Record<string, string> },
      ),
  };

  // User endpoints
  users = {
    get: (username: string) => this.request<UserProfile>(`/users/${username}`),

    updateProfile: (data: { displayName?: string; bio?: string; avatarUrl?: string }) =>
      this.request<User>('/users/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    getPreferences: () => this.request<UserPreferences>('/users/me/preferences'),

    updatePreferences: (data: Partial<UserPreferences>) =>
      this.request<UserPreferences>('/users/me/preferences', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    follow: (username: string) =>
      this.request<{ following: boolean }>(`/users/${username}/follow`, { method: 'POST' }),

    unfollow: (username: string) =>
      this.request<{ following: boolean }>(`/users/${username}/follow`, { method: 'DELETE' }),

    getFollowers: (username: string, limit?: number) =>
      this.request<{ items: User[] }>(`/users/${username}/followers`, { params: { limit } }),

    getFollowing: (username: string, limit?: number) =>
      this.request<{ items: User[] }>(`/users/${username}/following`, { params: { limit } }),

    getStreak: () => this.request<UserStreak>('/users/me/streak'),

    recordActivity: () =>
      this.request<UserStreak>('/users/me/streak/record', { method: 'POST' }),

    followTopic: (type: 'brand' | 'topic', value: string) =>
      this.request<{ following: boolean; value: string; type: string }>('/users/me/follow-topic', {
        method: 'POST',
        body: JSON.stringify({ type, value }),
      }),

    getLeaderboard: (limit?: number) =>
      this.request<{ items: LeaderboardEntry[] }>('/users/leaderboard', { params: { limit } }),

    // GROWTH-020: Picks
    getPicks: (username: string, year: number) =>
      this.request<UserPicksResponse>(`/users/${username}/picks/${year}`),

    savePicks: (year: number, data: { carPicks: PickItem[]; bikePicks: PickItem[] }) =>
      this.request<{ success: boolean }>(`/users/me/picks/${year}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // COMMUNITY-001: Public garage profile
    getGarageProfile: (username: string) =>
      this.request<GarageProfile>(`/users/${username}/garage-profile`),

    // COMMUNITY-003: Who to follow suggestions
    getSuggested: (limit?: number) =>
      this.request<{ items: SuggestedUser[] }>('/users/suggested', { params: { limit } }),
  };

  // Source endpoints
  sources = {
    list: (params?: { search?: string; category?: string; type?: string; limit?: number }) =>
      this.request<{ items: SourceWithStats[] }>('/sources', { params: params as Record<string, string> }),

    get: (id: string) => this.request<SourceWithStats>(`/sources/${id}`),

    follow: (id: string) =>
      this.request<{ following: boolean }>(`/sources/${id}/follow`, { method: 'POST' }),

    unfollow: (id: string) =>
      this.request<{ following: boolean }>(`/sources/${id}/follow`, { method: 'DELETE' }),

    following: () => this.request<{ items: SourceWithStats[] }>('/sources/following'),

    bulkFollow: (sourceIds: string[], action: 'follow' | 'unfollow') =>
      this.request<{ followed?: number; alreadyFollowing?: number; unfollowed?: number }>(
        '/sources/bulk-follow',
        {
          method: 'POST',
          body: JSON.stringify({ sourceIds, action }),
        }
      ),
  };

  // Search endpoints
  search = {
    query: (params: { q: string; type?: string; limit?: number }) =>
      this.request<SearchResults>('/search', {
        params: Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
        ),
      }),

    suggestions: (q: string) =>
      this.request<{ suggestions: SearchSuggestion[] }>('/search/suggestions', { params: { q } }),

    trending: () => this.request<TrendingData>('/search/trending'),
  };

  // Trending posts
  trending = {
    posts: (params?: { limit?: number; days?: number; hours?: number }) =>
      this.request<{ items: TrendingPost[] }>('/trending', { params: params as Record<string, string> }),

    tags: (params?: { limit?: number; hours?: number }) =>
      this.request<{ items: TrendingTag[] }>('/trending/tags', { params: params as Record<string, string> }),
  };

  // UI-041: Community Buzz — Reddit top posts
  community = {
    buzz: (params?: { limit?: number }) =>
      this.request<{ items: CommunityBuzzPost[] }>('/community/buzz', { params: params as Record<string, string> }),

    // COMM-013: Hot Discussion — top-engaged posts for a vehicle
    hotDiscussions: (params: { brand?: string; q: string; limit?: number }) =>
      this.request<{ items: HotDiscussionPost[] }>('/community/hot-discussions', {
        params: Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
        ),
      }),
  };

  // Subscription endpoints
  subscriptions = {
    checkout: () =>
      this.request<{ url: string }>('/subscriptions/checkout', { method: 'POST' }),

    portal: () =>
      this.request<{ url: string }>('/subscriptions/portal', { method: 'POST' }),

    status: () =>
      this.request<SubscriptionStatus>('/subscriptions/status'),
  };

  // Newsletter signup — public (no auth)
  newsletter = {
    subscribe: (data: { email: string; source?: string; category?: string }) =>
      this.request<{ subscribed: boolean }>('/newsletter/subscribe', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };

  // MON-019: Pro waitlist — public (no auth)
  waitlist = {
    join: (data: { email: string; name?: string; source?: string }) =>
      this.request<{ joined: boolean; count: number }>('/waitlist', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    count: () =>
      this.request<{ count: number }>('/waitlist/count'),
  };

  // Dealer test drive leads — public (no auth)
  dealerLeads = {
    submit: (data: { name: string; phone: string; city?: string; vehicleSlug: string; brand?: string; leadType?: 'dealer_quote' | 'test_drive' }) =>
      this.request<{ submitted: boolean }>('/dealer-leads', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };

  // Vehicle launch alerts — public (no auth)
  vehicleAlerts = {
    subscribe: (data: { email: string; vehicleSlug: string; vehicleType: string; brand?: string }) =>
      this.request<{ subscribed: boolean }>('/vehicle-alerts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };

  // GROWTH-022: WebPush launch day alerts
  launchPushAlerts = {
    subscribe: (data: { vehicleSlug: string; brand?: string }) =>
      this.request<{ subscribed: boolean; vehicleSlug: string }>('/launch-alerts/subscribe', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    unsubscribe: (data: { vehicleSlug: string }) =>
      this.request<{ subscribed: boolean; vehicleSlug: string }>('/launch-alerts/unsubscribe', {
        method: 'DELETE',
        body: JSON.stringify(data),
      }),
    my: () =>
      this.request<{ alerts: Array<{ id: string; vehicleSlug: string; brand: string | null; createdAt: string }> }>('/launch-alerts/my'),
    count: (slug: string) =>
      this.request<{ slug: string; subscribers: number }>(`/launch-alerts/${slug}/count`),
  };

  // MON-016: CRM lead handoff — convert NotifyMe subscriber to dealer lead
  crmLeads = {
    convert: (data: { name: string; phone: string; city?: string; vehicleSlug: string; brand?: string; leadType?: 'dealer_quote' | 'test_drive' }) =>
      this.request<{ converted: boolean }>('/crm/convert', {
        method: 'POST',
        body: JSON.stringify({ ...data, source: 'launch_handoff' }),
      }),
  };

  // MON-020: Pro Subsidy Alerts — deadline + volume-cap notifications (Pro only)
  subsidyAlerts = {
    list: () =>
      this.request<{ alerts: SubsidyAlert[] }>('/subsidy-alerts'),

    subscribe: (data: { vehicleSlug: string; alertTypes?: ('deadline' | 'volume_cap')[] }) =>
      this.request<{ subscribed: boolean; id: string }>('/subsidy-alerts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ unsubscribed: boolean }>(`/subsidy-alerts/${id}`, { method: 'DELETE' }),
  };

  // Price drop email alerts — public (no auth)
  priceAlerts = {
    subscribe: (data: { email: string; vehicleSlug: string; brand?: string }) =>
      this.request<{ subscribed: boolean }>('/price-alerts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };

  // Saved searches (Pro-only)
  savedSearches = {
    list: () =>
      this.request<{ items: SavedSearch[] }>('/saved-searches'),

    create: (data: { name: string; query: string; filters?: SavedSearchFilters; notifyOnNew?: boolean }) =>
      this.request<SavedSearch>('/saved-searches', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: { name?: string; notifyOnNew?: boolean; filters?: SavedSearchFilters }) =>
      this.request<SavedSearch>(`/saved-searches/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ deleted: boolean }>(`/saved-searches/${id}`, { method: 'DELETE' }),
  };

  // Hot Take reactions (public, no auth)
  reactions = {
    getHotTake: (postId: string, visitorId: string) =>
      this.request<{ hot: number; cold: number; userVote: string | null }>(
        `/reactions/hot-take/${postId}`,
        { params: { visitorId } }
      ),

    castHotTake: (postId: string, voteType: 'hot' | 'cold', visitorId: string) =>
      this.request<{ hot: number; cold: number; userVote: string }>(
        `/reactions/hot-take/${postId}`,
        { method: 'POST', body: JSON.stringify({ voteType, visitorId }) }
      ),

    getArticle: (postId: string, visitorId: string) =>
      this.request<ArticleReactionResponse>(
        `/reactions/article/${postId}`,
        { params: { visitorId } }
      ),

    reactArticle: (postId: string, reactionType: ArticleReactionType, visitorId: string) =>
      this.request<ArticleReactionResponse>(
        `/reactions/article/${postId}`,
        { method: 'POST', body: JSON.stringify({ reactionType, visitorId }) }
      ),
  };

  // Notification endpoints
  notifications = {
    list: (params?: { cursor?: string; limit?: number; unread?: boolean }) =>
      this.request<PaginatedResponse<Notification>>('/notifications', { params: params as Record<string, string> }),

    unreadCount: () => this.request<{ count: number }>('/notifications/unread-count'),

    markRead: (id: string) =>
      this.request<{ success: boolean }>(`/notifications/${id}/read`, { method: 'POST' }),

    markAllRead: () =>
      this.request<{ success: boolean }>('/notifications/read-all', { method: 'POST' }),

    registerPush: (data: { endpoint: string; keys: { p256dh: string; auth: string }; platform: 'web' | 'ios' | 'android' }) =>
      this.request<{ success: boolean }>('/notifications/push-subscription', { method: 'POST', body: JSON.stringify(data) }),

    unregisterPush: (endpoint: string) =>
      this.request<{ success: boolean }>(`/notifications/push-subscription?endpoint=${encodeURIComponent(endpoint)}`, { method: 'DELETE' }),

    // COMMUNITY-004: Notification digest — grouped summary
    digest: (hours?: number) =>
      this.request<NotificationDigest>(`/notifications/digest${hours ? `?hours=${hours}` : ''}`),
  };

  // Vehicle ratings & reviews
  vehicleRatings = {
    get: (slug: string, visitorId?: string) =>
      this.request<VehicleRatingResponse>(`/vehicle-ratings/${encodeURIComponent(slug)}`, {
        params: visitorId ? { visitorId } : undefined,
      }),

    submitReview: (slug: string, data: { rating: number; text?: string; visitorId: string }) =>
      this.request<{ review: { id: string; rating: number; text: string | null }; action: string }>(
        `/vehicle-ratings/${encodeURIComponent(slug)}/reviews`,
        { method: 'POST', body: JSON.stringify(data) }
      ),

    deleteReview: (slug: string, reviewId: string, visitorId: string) =>
      this.request<{ success: boolean }>(
        `/vehicle-ratings/${encodeURIComponent(slug)}/reviews/${reviewId}?visitorId=${encodeURIComponent(visitorId)}`,
        { method: 'DELETE' }
      ),
  };

  // Vehicle ownership — COMM-010
  vehicleOwners = {
    get: (slug: string, limit?: number) =>
      this.request<VehicleOwnerResponse>(`/vehicle-owners/${encodeURIComponent(slug)}`, {
        params: limit ? { limit } : undefined,
      }),

    claim: (slug: string, data: { vehicleType: string; brand: string }) =>
      this.request<{ owner: { id: string; claimedAt: string }; action: string }>(
        `/vehicle-owners/${encodeURIComponent(slug)}/claim`,
        { method: 'POST', body: JSON.stringify(data) }
      ),

    unclaim: (slug: string) =>
      this.request<{ success: boolean }>(
        `/vehicle-owners/${encodeURIComponent(slug)}/claim`,
        { method: 'DELETE' }
      ),
  };

  // COMM-012: Weekly digest — public (no auth) + TECH-002: digest email prefs (auth)
  digest = {
    weekly: (params?: { limit?: number }) =>
      this.request<WeeklyDigest>('/digest/weekly', { params: params as Record<string, string> }),
    preferences: () =>
      this.request<DigestPreferences>('/digest/preferences'),
    subscribe: () =>
      this.request<{ success: boolean; digestEnabled: boolean }>('/digest/subscribe', { method: 'POST' }),
    unsubscribe: () =>
      this.request<{ success: boolean; digestEnabled: boolean }>('/digest/unsubscribe', { method: 'POST' }),
  };

  // TECH-001: Public status page — system health checks
  status = {
    get: () => this.request<SystemStatus>('/status'),
  };

  // GROWTH-005: EV charging station finder
  evCharging = {
    stations: (params?: { lat?: number; lng?: number; limit?: number }) =>
      this.request<ChargingStationsResponse>('/ev-charging/stations', { params: params as Record<string, string> }),
  };

  // AI-008: AI Vehicle Comparison Generator
  compare = {
    generate: (v1: string, v2: string) =>
      this.request<CompareGenerateResponse>('/compare/generate', { params: { v1, v2 } }),
  };

  // GROWTH-015: India Auto Market Live Dashboard
  indiaAutoSales = {
    latest: () =>
      this.request<IndiaAutoSalesLatestResponse>('/india-auto-sales/latest'),
    trends: () =>
      this.request<IndiaAutoSalesTrendsResponse>('/india-auto-sales/trends'),
  };

  // Admin endpoints
  admin = {
    aiQuality: (params?: { days?: number }) =>
      this.request<Record<string, unknown>>('/admin/ai-quality', { params: params as Record<string, string> }),

    shareKfactor: (params?: { days?: number }) =>
      this.request<ShareKfactorResponse>('/admin/share-kfactor', { params: params as Record<string, string> }),

    affiliateRevenue: (params?: { days?: number }) =>
      this.request<AffiliateRevenueResponse>('/admin/affiliate-revenue', { params: params as Record<string, string> }),

    affiliateArticleBreakdown: (params?: { days?: number }) =>
      this.request<AffiliateArticleBreakdownResponse>('/admin/affiliate-article-breakdown', { params: params as Record<string, string> }),

    proFunnel: (params?: { days?: number }) =>
      this.request<ProFunnelResponse>('/admin/analytics/pro-funnel', { params: params as Record<string, string> }),

    shareClicks: (params?: { days?: number }) =>
      this.request<ShareClicksResponse>('/admin/share-clicks', { params: params as Record<string, string> }),
  };
}

export const api = new ApiClient(API_URL);

// MON-020: Pro Subsidy Alert
export interface SubsidyAlert {
  id: string;
  vehicleSlug: string;
  alertTypes: string;
  isActive: boolean;
  createdAt: string;
}

// ANALYTICS-020: Share destination breakdown response
export interface ShareChannelBreakdown {
  channel: string;
  shares: number;
  arrivals: number;
  uniqueArrivals: number;
  conversionRate: number;
  kFactor: number;
}

export interface ShareKfactorResponse {
  days: number;
  since: string;
  totalShares: number;
  totalArrivals: number;
  overallKFactor: number;
  breakdown: ShareChannelBreakdown[];
}

// ANALYTICS-063: Share click surface breakdown
export interface ShareClickEntry {
  channel: string;
  surface: string;
  clicks: number;
  pct: number;
}

export interface ShareClicksResponse {
  days: number;
  since: string;
  totalClicks: number;
  breakdown: ShareClickEntry[];
}

// ANALYTICS-057: Affiliate CTA Revenue Attribution
export interface AffiliateRevenueResponse {
  days: number;
  since: string;
  totalClicks: number;
  totalImpressions: number;
  ctr: number;
  channels: Record<string, { clicks: number; partners: Record<string, number> }>;
  dailyTrend: Array<{ day: string; channel: string; clicks: number }>;
  impressions: Array<{ event: string; impressions: number }>;
}

export interface AffiliateArticleBreakdownResponse {
  days: number;
  since: string;
  articleTotals: Array<{ articleId: string; articleSlug: string; clicks: number }>;
  breakdown: Array<{ articleId: string; articleSlug: string; partnerName: string; position: string; clicks: number }>;
}

// ANALYTICS-059: Pro Checkout Funnel by UTM Source
export interface ProFunnelSourceRow {
  source: string;
  views: number;
  checkoutStarts: number;
  completions: number;
  abandons: number;
  viewToCheckoutRate: number;
  checkoutToCompleteRate: number;
}

export interface ProFunnelResponse {
  days: number;
  since: string;
  sourceCount: number;
  totals: {
    views: number;
    checkoutStarts: number;
    completions: number;
    abandons: number;
    viewToCheckoutRate: number;
    checkoutToCompleteRate: number;
  };
  sources: ProFunnelSourceRow[];
}

// Types
export interface User {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  reputation: number;
  streakDays: number;
  isVerified: boolean;
  isPro: boolean;
  isAdmin: boolean;
}

export interface UserProfile extends User {
  createdAt: string;
  followerCount: number;
  followingCount: number;
  isFollowing: boolean;
  isCurrentUser: boolean;
  streak: UserStreak | null;
}

// COMMUNITY-003: Suggested user for "Who to Follow"
export interface SuggestedUser {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  reputation: number;
  isVerified: boolean;
  isPro: boolean;
  followerCount: number;
}

// COMMUNITY-001: Public garage profile
export interface GarageProfileMembership {
  garageId: string;
  name: string;
  slug: string;
  emoji: string | null;
  category: string | null;
  memberCount: number;
  role: string;
  joinedAt: string;
}

export interface GarageProfilePost {
  id: string;
  title: string | null;
  content: string | null;
  upvoteCount: number;
  commentCount: number;
  viewCount: number;
  createdAt: string;
  garageName: string;
  garageSlug: string;
  garageEmoji: string | null;
}

export interface GarageProfile {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    bio: string | null;
    reputation: number;
    isVerified: boolean;
    isPro: boolean;
    createdAt: string;
  };
  garages: GarageProfileMembership[];
  recentPosts: GarageProfilePost[];
  stats: {
    garageCount: number;
    totalPosts: number;
    totalUpvotes: number;
    totalComments: number;
  };
  // COMMUNITY-002: Follow state
  followerCount: number;
  followingCount: number;
  isFollowing: boolean;
  isCurrentUser: boolean;
}

export interface UserStreak {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  freezeTokensRemaining: number;
}

export interface LeaderboardEntry {
  rank: number;
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  reputation: number;
  isVerified: boolean;
  isPro: boolean;
  currentStreak: number;
  longestStreak: number;
}

export type PushCategoryKey =
  | 'car_launches'
  | 'price_drops'
  | 'race_results'
  | 'ev_news'
  | 'streak_reminders';

export type PushFrequency = 'instant' | 'daily' | 'weekly' | 'off';
export type PushFrequencies = Partial<Record<PushCategoryKey, PushFrequency>>;

export interface UserPreferences {
  preferredCategories: string[];
  followedBrands: string[];
  followedTopics: string[];
  emailNotifications: boolean;
  pushNotifications: boolean;
  pushCategories: PushCategoryKey[];
  pushFrequencies: PushFrequencies;
  theme: 'dark' | 'light' | 'system';
}

export interface Source {
  id: string;
  name: string;
  url: string;
  logoUrl: string | null;
  sourceType: string;
  category: string;
  reputationScore?: number;
}

export interface SourceWithStats extends Source {
  feedUrl?: string;
  reputationScore?: number;
  isFollowing?: boolean;
  postCount?: number;
  totalUpvotes?: number;
  followerCount?: number;
}

export interface Post {
  id: string;
  title: string;
  url: string;
  thumbnailUrl: string | null;
  excerpt: string | null;
  contentType: 'article' | 'video' | 'discussion';
  category: 'cars' | 'motorcycles' | 'scooters' | 'ev' | 'adv';
  tags: string[];
  brands: string[];
  vehicleTypes: string[];
  readTime: number | null;
  duration: number | null;
  upvoteCount: number;
  downvoteCount: number;
  commentCount: number;
  bookmarkCount: number;
  viewCount: number;
  publishedAt: string;
  source: Source | null;
  userInteraction?: UserInteraction;
  // T-8: full body for MotoWalla-original editorial (personas). Present on the
  // detail response only; aggregated posts leave it null and link out instead.
  cleanContent?: string | null;
  // AI-generated content
  author?: string | null;
  // T-130: `author` is a display STRING with no id behind it, so the profile
  // link target has to be resolved server-side. Detail response only; null when
  // the byline does not match a real user (every aggregated post). Never build
  // a /user/ URL from `author` — that is how /source/undefined shipped (T-125).
  authorUsername?: string | null;
  aiSummary?: string | null;
  aiSummaryShort?: string | null;
  hasAiSummary?: boolean;
  aiEntities?: {
    brands?: string[];
    vehicles?: string[];
    vehicle_types?: string[];
    price_range?: string | null;
    topics?: string[];
  } | null;
  aiRelevanceScore?: number | null;
  headlineRewritten?: boolean;
  originalTitle?: string | null;
  garageActivityCount?: number;
  garageCount?: number;
  // AI-001: Garage-matched personalization — user's garage that matches this post
  garageMatch?: {
    slug: string;
    name: string;
    emoji: string | null;
    memberCount: number;
  } | null;
  isTrending?: boolean;
  brandFollowerCount?: number;
  topReaction?: ReactionType;
  reactionCount?: number;
  reactionCounts?: Partial<Record<ReactionType, number>>;
  // ANALYTICS-091: Sponsored feed card pre-wire for MON-001
  isSponsored?: boolean;
  sponsorBrand?: string;
  sponsorCtaType?: 'see_price' | 'book_test_drive';
  // UI-086: BNCAP safety rating (1-5 stars) when vehicle entity has a known rating
  bncapStars?: number;
}

export type ReactionType = 'fire' | 'like' | 'interesting';

export type ArticleReactionType = 'hot' | 'agree' | 'surprised' | 'thinking' | 'disagree';

// AI-003: Vehicle entity feed clustering
export interface VehicleClusterPreviewPost {
  id: string;
  title: string;
  url: string;
  thumbnailUrl: string | null;
  contentType: string;
  category: string;
  upvoteCount: number;
  commentCount: number;
  publishedAt: string;
  source: { name: string; logoUrl: string | null } | null;
}

export interface VehicleCluster {
  vehicle: string;
  brand: string;
  slug: string;
  postCount: number;
  totalEngagement: number;
  previewPosts: VehicleClusterPreviewPost[];
}

export interface ArticleReactionResponse {
  counts: Record<ArticleReactionType, number>;
  userReaction: ArticleReactionType | null;
}

export interface UserInteraction {
  upvoted: boolean;
  downvoted: boolean;
  bookmarked: boolean;
  viewed?: boolean;
  notRelevant?: boolean;
  reaction?: ReactionType | null;
}

export interface DiscussingGarage {
  id: string;
  name: string;
  slug: string;
  emoji: string | null;
  memberCount: number;
  isMember: boolean;
}

export interface Comment {
  id: string;
  postId: string;
  content: string;
  highlightedText: string | null;
  upvoteCount: number;
  downvoteCount: number;
  createdAt: string;
  // T-197: NULL when the author deleted their account. The comment text and its
  // thread position survive (we anonymise rather than cascade), so this field
  // is genuinely optional and every reader must handle it. Typed nullable on
  // purpose: tsc is what finds the renderers that would otherwise throw.
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    reputation: number;
  } | null;
  parentId: string | null;
}

export type CommentReactionEmoji = '👍' | '🔥' | '❤️';

export interface CommentReactionData {
  counts: Record<string, number>;
  userReactions: string[];
}

export interface CommentReactionToggleResult extends CommentReactionData {
  toggled: boolean;
}

export interface BulkCommentReactions {
  reactions: Record<string, CommentReactionData>;
}

export interface Garage {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  emoji: string | null;
  category: string | null;
  isPublic: boolean;
  memberCount: number;
  postCount: number;
  postsLast7Days?: number;
  lastPostAt?: string | null;
  isMember?: boolean;
  role?: string | null;
}

export interface TrendingDiscussion {
  id: string;
  title: string | null;
  content: string | null;
  upvoteCount: number;
  commentCount: number;
  createdAt: string;
  user: { id: string; username: string; displayName: string | null; avatarUrl: string | null };
  garage: { id: string; name: string; slug: string; emoji: string | null };
  post: { id: string; title: string; imageUrl: string | null; source: { name: string } | null } | null;
}

export interface GaragePost {
  id: string;
  garageId: string;
  title: string | null;
  content: string | null;
  imageUrls: string[] | null;
  upvoteCount: number;
  commentCount: number;
  viewCount: number;
  createdAt: string;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    reputation: number;
  };
  post: Post | null;
}

// ENGAGE-014: Garage discussion thread linked to an article
export interface GarageThread {
  id: string;
  title: string | null;
  content: string | null;
  upvoteCount: number;
  commentCount: number;
  createdAt: string;
  garage: {
    id: string;
    name: string;
    slug: string;
    emoji: string | null;
    memberCount: number;
    isMember: boolean;
  };
  topComments: Array<{
    id: string;
    content: string;
    upvoteCount: number;
    createdAt: string;
    user: {
      id: string;
      username: string;
      displayName: string | null;
      avatarUrl: string | null;
      reputation: number;
    };
  }>;
}

export interface GaragePostComment {
  id: string;
  garagePostId: string;
  parentId: string | null;
  content: string;
  upvoteCount: number;
  downvoteCount: number;
  createdAt: string;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    reputation: number;
  };
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  referenceType: string | null;
  referenceId: string | null;
  isRead: boolean;
  createdAt: string;
  actor: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
}

// COMMUNITY-004: Notification digest types
export interface NotificationDigestGroup {
  type: string;
  count: number;
  unreadCount: number;
  actors: Array<{ id: string; username: string; displayName: string | null; avatarUrl: string | null }>;
  latest: Notification | null;
}

export interface NotificationDigest {
  period: { since: string; hours: number };
  totalCount: number;
  unreadCount: number;
  groups: NotificationDigestGroup[];
}

export interface FeedParams {
  type?: 'my-feed' | 'popular' | 'upvoted' | 'latest' | 'discussed' | 'hot';
  period?: 'day' | 'week' | 'month' | 'all';
  category?: 'cars' | 'motorcycles' | 'scooters' | 'ev' | 'adv' | 'motorsport' | 'launches' | 'all';
  contentType?: 'article' | 'video' | 'discussion' | 'all';
  language?: 'english' | 'hindi' | 'hinglish' | 'all';
  sources?: 'following' | 'all';
  sourceId?: string;
  tag?: string;
  make?: string;
  fuelType?: 'all' | 'ev' | 'phev' | 'hybrid' | 'flex-fuel' | 'petrol' | 'diesel';
  cursor?: string;
  offset?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  nextCursor: string | null;
  nextOffset?: number | null;
}

export interface SearchResults {
  posts?: Post[];
  sources?: Source[];
  garages?: Garage[];
  users?: UserProfile[];
}

export interface SearchSuggestion {
  type: 'post' | 'garage';
  id?: string;
  slug?: string;
  title?: string;
  name?: string;
  emoji?: string;
}

export interface TrendingData {
  tags: { tag: string; count: number }[];
  brands: { brand: string; count: number }[];
  searches: string[];
}

export interface TrendingTag {
  tag: string;
  count: number;
  heat: 'hot' | 'rising' | 'steady';
  priorCount?: number;
  changePercent?: number;
}

export interface TrendingPost {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  contentType: string;
  category: string;
  upvoteCount: number;
  commentCount: number;
  viewCount: number;
  bookmarkCount: number;
  publishedAt: string;
  /** T-161: the byline the projection now carries. */
  author: string | null;
  /** Resolved only when the byline matches one of our writers; else null. */
  authorUsername: string | null;
  sourceName: string | null;
  sourceLogoUrl: string | null;
  totalReadingTimeMs: number;
  readerCount: number;
  engagementScore: number;
}

export interface BriefingItem {
  rank: number;
  id: string;
  title: string;
  summary: string | null;
  thumbnailUrl: string | null;
  category: string | null;
  brands: string[] | null;
  contentType: string;
  viewCount: number;
  commentCount: number;
  publishedAt: string;
  // T-140: both card endpoints emit posts and therefore owe a byline. Resolved
  // server-side via lib/author-link; null authorUsername = no profile, render text.
  author: string | null;
  authorUsername: string | null;
  sourceName: string | null;
  garageMatch?: boolean;
}

// ENGAGE-015: Morning Dispatch types
export interface DispatchItem {
  rank: number;
  id: string;
  title: string;
  summary: string | null;
  hook: string | null;
  thumbnailUrl: string | null;
  category: string | null;
  brands: string[] | null;
  contentType: string;
  viewCount: number;
  commentCount: number;
  publishedAt: string;
  author: string | null;
  authorUsername: string | null;
  sourceName: string | null;
  garageMatch: boolean;
}

export interface DispatchSection {
  label: string;
  items: DispatchItem[];
}

export interface DispatchResponse {
  headline: string;
  intro: string;
  greeting: string | null;
  date: string;
  sections: DispatchSection[];
  personalized: boolean;
  totalStories: number;
  generatedAt: string;
}

// AI-014: Entity-matched related article
// COMMUNITY-002: "Popular in Your Garages" feed section
export interface GarageTrendingItem {
  id: string;
  title: string;
  url: string;
  thumbnailUrl: string | null;
  excerpt: string | null;
  contentType: string;
  category: string;
  brands: string[] | null;
  upvoteCount: number;
  commentCount: number;
  viewCount: number;
  publishedAt: string;
  source: { name: string; logoUrl: string | null } | null;
  trendingInGarage: { id: string; name: string; slug: string; emoji: string | null; memberCount: number } | null;
  garageEngagement: { shares: number; upvotes: number; comments: number };
}

export interface GarageTrendingResponse {
  items: GarageTrendingItem[];
  garages: Array<{ id: string; name: string; slug: string; emoji: string | null }>;
}

export interface RelatedArticle {
  id: string;
  title: string;
  url: string;
  thumbnailUrl: string | null;
  excerpt: string | null;
  contentType: string;
  category: string;
  brands: string[] | null;
  upvoteCount: number;
  commentCount: number;
  viewCount: number;
  publishedAt: string;
  source: { name: string; logoUrl: string | null } | null;
  matchScore: number;
}

export interface CommunityBuzzPost {
  title: string;
  permalink: string;
  score: number;
  numComments: number;
  subreddit: string;
  createdUtc: number;
  author: string;
}

export interface HotDiscussionPost {
  id: string;
  title: string;
  url: string;
  thumbnailUrl: string | null;
  snippet: string | null;
  contentType: string;
  category: string;
  commentCount: number;
  upvoteCount: number;
  reactionCount: number;
  engagementScore: number;
  publishedAt: string;
  sourceName: string | null;
}

export interface TrendingGarage {
  id: string;
  name: string;
  slug: string;
  emoji: string | null;
  category: string | null;
  memberCount: number;
  postCount: number;
  imageUrl: string | null;
  newPosts: number;
  newMembers: number;
  totalEngagement: number;
  activityScore: number;
}

export interface SpotlightEntry {
  id: string;
  createdAt: string;
  title: string | null;
  user: { id: string; username: string; displayName: string | null; avatarUrl: string | null };
  garage: { id: string; name: string; slug: string; emoji: string | null; imageUrl: string | null };
  post: { id: string; title: string; imageUrl: string | null; source: { name: string } | null } | null;
}

export interface GarageCountResponse {
  count: number;
  garages: Array<{ slug: string; name: string; emoji: string | null; memberCount: number }>;
}

export interface VehicleRatingResponse {
  averageRating: number;
  totalReviews: number;
  ratingCounts: Record<number, number>;
  reviews: Array<{
    id: string;
    rating: number;
    text: string | null;
    createdAt: string;
    isOwn: boolean;
  }>;
  userReview: { id: string; rating: number; text: string | null } | null;
}

export interface VehicleOwnerResponse {
  ownerCount: number;
  owners: Array<{
    id: string;
    claimedAt: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    reputation: number;
    isVerified: boolean;
  }>;
}

export interface GarageActivityEvent {
  id: string;
  type: 'join' | 'post';
  timestamp: string;
  garageId: string;
  garageName: string;
  garageSlug: string;
  garageEmoji: string | null;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  postTitle: string | null;
  postId: string | null;
}

// COMM-017: Garage-specific activity event (includes comments)
export interface GarageSlugActivityEvent {
  id: string;
  type: 'join' | 'post' | 'comment';
  timestamp: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  postTitle: string | null;
  postId: string | null;
  commentContent: string | null;
}

// COMM-011: Co-membership discovery
export interface GarageAlsoInItem {
  id: string;
  name: string;
  slug: string;
  emoji: string | null;
  category: string | null;
  memberCount: number;
  imageUrl: string | null;
  sharedMembers: number;
}

// COMM-010: Garage reading activity stream
export interface GarageReadingEvent {
  id: string;
  type: 'view' | 'bookmark' | 'upvote';
  readAt: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  post: {
    id: string;
    title: string;
    thumbnailUrl: string | null;
    category: string;
    contentType: string;
    sourceName: string | null;
    sourceLogoUrl: string | null;
  };
}

export interface SubscriptionStatus {
  isPro: boolean;
  subscription: {
    status: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  } | null;
}

export interface SavedSearchFilters {
  category?: string;
  contentType?: string;
  brands?: string[];
  tags?: string[];
  sourceId?: string;
}

export interface SavedSearch {
  id: string;
  userId: string;
  name: string;
  query: string;
  filters: SavedSearchFilters;
  notifyOnNew: boolean;
  lastCheckedAt: string | null;
  newResultCount: number;
  createdAt: string;
  updatedAt: string;
}

// COMM-012: Weekly Digest types
export interface DigestArticle {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  excerpt: string | null;
  contentType: string;
  category: string;
  tags: string[];
  brands: string[];
  readTime: number | null;
  upvoteCount: number;
  commentCount: number;
  viewCount: number;
  bookmarkCount: number;
  publishedAt: string;
  aiSummaryShort: string | null;
  sourceName: string | null;
  sourceLogoUrl: string | null;
  engagementScore: number;
}

export interface DigestDiscussion {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  category: string;
  commentCount: number;
  upvoteCount: number;
  publishedAt: string;
  sourceName: string | null;
}

export interface DigestGarage {
  id: string;
  name: string;
  slug: string;
  emoji: string | null;
  category: string | null;
  memberCount: number;
  imageUrl: string | null;
  newPosts: number;
}

export interface DigestTag {
  tag: string;
  postCount: number;
}

export interface ServiceCheck {
  status: 'operational' | 'degraded' | 'down';
  latencyMs?: number;
  error?: string;
}

export interface SystemStatus {
  overall: 'operational' | 'degraded' | 'down';
  checks: Record<string, ServiceCheck>;
  uptime: number;
  checkedAt: string;
}

export interface ChargingStation {
  id: string;
  name: string;
  network: string;
  address: string;
  lat: number;
  lng: number;
  connectorTypes: ('AC' | 'DC')[];
  power_kw: number;
  status: 'operational' | 'offline' | 'unknown';
  cost_per_kwh?: number;
  distanceKm?: number;
}

export interface ChargingStationsResponse {
  stations: ChargingStation[];
  total: number;
  mode: 'nearby' | 'featured';
  userLocation?: { lat: number; lng: number };
  cities?: string[];
}

export interface WeeklyDigest {
  weekStart: string;
  weekEnd: string;
  topArticles: DigestArticle[];
  mostDiscussed: DigestDiscussion[];
  activeGarages: DigestGarage[];
  topTags: DigestTag[];
  stats: {
    totalPosts: number;
    totalUpvotes: number;
    totalComments: number;
    totalViews: number;
  };
}

// AI-008: AI Vehicle Comparison Generator
export interface ComparisonSpec {
  spec: string;
  vehicle1Value: string;
  vehicle2Value: string;
  edge: '1' | '2' | 'tie';
}

export interface ComparisonResult {
  vehicle1: string;
  vehicle2: string;
  vehicleType: 'car' | 'bike' | 'scooter' | 'ev';
  tagline: string;
  specs: ComparisonSpec[];
  prosVehicle1: string[];
  prosVehicle2: string[];
  verdict: string;
  faqs: Array<{ q: string; a: string }>;
  generatedAt: string;
}

export interface CompareGenerateResponse {
  comparison: ComparisonResult;
  cached: boolean;
}

// GROWTH-015: India Auto Market Live Dashboard
export interface IndiaAutoSalesSegment {
  segment: string;
  units: number;
  yoy: number;
  trend: 'up' | 'down' | 'flat';
}

export interface IndiaAutoSalesBrand {
  brand: string;
  units: number;
  share: number;
  yoy: number;
  trend: 'up' | 'down' | 'flat';
}

export interface IndiaAutoSalesMonth {
  month: string;
  totalUnits: number;
  totalYoy: number;
  segments: IndiaAutoSalesSegment[];
  pvTop10: IndiaAutoSalesBrand[];
  twTop5: IndiaAutoSalesBrand[];
  evPenetration: { pvPercent: number; twPercent: number; pvUnits: number; twUnits: number };
}

export interface IndiaAutoSalesLatestResponse {
  data: IndiaAutoSalesMonth;
  updatedAt: string;
  source: string;
}

export interface IndiaAutoSalesTrend {
  month: string;
  pvUnits: number;
  twUnits: number;
  evPvPercent: number;
}

export interface IndiaAutoSalesTrendsResponse {
  trends: IndiaAutoSalesTrend[];
  updatedAt: string;
  source: string;
}

// TECH-002: Digest email preferences
export interface DigestPreferences {
  digestEnabled: boolean;
  digestLastSentAt: string | null;
  isPro: boolean;
  email: string | null;
}

// GROWTH-020: User picks
export interface PickItem {
  slug: string;
  name: string;
  brand: string;
}

export interface UserPicksResponse {
  user: { username: string; displayName: string | null; avatarUrl: string | null };
  year: number;
  carPicks: PickItem[];
  bikePicks: PickItem[];
}
